import { useMemo, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { Box, Group, ScrollArea, Stack, Text, TextInput, ThemeIcon, UnstyledButton } from '@mantine/core';
import {
  IconApi,
  IconGitBranch,
  IconKeyboard,
  IconMessageCircle,
  IconMicrophone,
  IconPhoneOff,
  IconPlayerPlay,
  IconRecordMail,
  IconRobot,
  IconSearch,
} from '@tabler/icons-react';
import { widgetRegistry } from '@/widgets/registry';
import type { WidgetCategory } from '@/widgets/types';

const categoryLabels: Record<WidgetCategory, string> = {
  voice: 'Voice & Audio',
  input: 'Input',
  routing: 'Routing',
  integration: 'Integrations',
  control: 'Control',
};

const categoryIcons: Record<WidgetCategory, ReactNode> = {
  voice: <IconMicrophone size={14} />,
  input: <IconKeyboard size={14} />,
  routing: <IconGitBranch size={14} />,
  integration: <IconApi size={14} />,
  control: <IconPhoneOff size={14} />,
};

const widgetIcons: Record<string, typeof IconMessageCircle> = {
  say: IconMessageCircle,
  gather: IconKeyboard,
  play: IconPlayerPlay,
  recording: IconRecordMail,
  transfer: IconGitBranch,
  api: IconApi,
  ai: IconRobot,
};

type DraggableWidgetProps = {
  id: string;
  name: string;
  description: string;
};

function DraggableWidget({ id, name, description }: DraggableWidgetProps) {
  const Icon = widgetIcons[id] ?? IconGitBranch;

  return (
    <UnstyledButton
      draggable
      onDragStart={(event: DragEvent) => {
        event.dataTransfer.setData('application/reactflow', id);
        event.dataTransfer.effectAllowed = 'move';
      }}
      p="xs"
      style={{ borderRadius: 'var(--mantine-radius-sm)', cursor: 'grab', transition: 'background-color 150ms ease' }}
      className="widget-palette-item"
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon variant="light" size="md" color="indigo">
          <Icon size={14} />
        </ThemeIcon>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500}>
            {name}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {description}
          </Text>
        </div>
      </Group>
    </UnstyledButton>
  );
}

export function WidgetPalette({ enabledWidgets }: { enabledWidgets?: string[] }) {
  const [search, setSearch] = useState('');

  const categories = useMemo(() => {
    const term = search.trim().toLowerCase();

    return widgetRegistry
      .getMetadataByCategory()
      .map((category) => ({
        ...category,
        widgets: category.widgets.filter((w) => {
          if (w.tier === 'system') return false;
          if (enabledWidgets && !enabledWidgets.includes(w.id)) return false;

          return !term || w.name.toLowerCase().includes(term) || w.description.toLowerCase().includes(term);
        }),
      }))
      .filter((category) => category.widgets.length > 0);
  }, [search, enabledWidgets]);

  return (
    <Stack gap={0} h="100%">
      <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <TextInput
          placeholder="Search widgets..."
          leftSection={<IconSearch size={14} />}
          size="xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <ScrollArea flex={1}>
        <Stack gap="xs" p="sm">
          {categories.map((category) => (
            <Box key={category.category}>
              <Group gap="xs" mb="xs">
                {categoryIcons[category.category]}
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  {categoryLabels[category.category]}
                </Text>
              </Group>

              <Stack gap={4}>
                {category.widgets.map((widget) => (
                  <DraggableWidget key={widget.id} id={widget.id} name={widget.name} description={widget.description} />
                ))}
              </Stack>
            </Box>
          ))}

          {categories.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              No widgets found
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
